import { LockOutlined, PlusOutlined, ScanOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Form, Input, Modal, Row } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { QrReader } from "react-qr-reader";
import { useSearchParams } from "react-router-dom";
import {
	getAllInvoices,
	getPendingSyncInvoices,
	getTodaysInvoicesByType,
	getCompleteInvoice,
	getCreditMemosForInvoice,
	getInvoiceByNo,
} from "../../services/invoiceService";
import { getClientByNoOrId } from "../../services/clientService";
import {
	getFinalDocumentNo,
	refreshFinalDocumentNumbersForList,
} from "../../services/invoiceSyncService";
import { getInvoicePaymentLines } from "../../services/invoicePaymentService";
import InvoiceForm from "./InvoiceForm";
import InvoiceList from "./InvoiceList";
import { getInvoiceListConfig } from "./configs/invoiceList.config";
import { useConfig } from "../../contexts/ConfigContext";
import { useAuth } from "../../contexts/AuthContext";
import { isCajero } from "../../config/rbac/rbac.config";
import db from "../../db/database";
import { COUNTRY_CODES } from "../../constants/countries";
import { matchesDocumentType } from "../../constants/documentType";
import { isEmpty, formatAxiosError } from "../../utils/common";
import "./InvoiceStyles.css";

const InvoiceManagement = ({ documentType = "Invoice", syncStatus = "" }) => {
	const { country } = useConfig();
	const { currentUser } = useAuth();
	const { showNotasCredito } = getInvoiceListConfig(country?.code);
	const [showForm, setShowForm] = useState(false);
	const [selectedInvoice, setSelectedInvoice] = useState(null);
	const [qrModalVisible, setQrModalVisible] = useState(false);
	const [invoiceNo, setInvoiceNo] = useState("");
	const [cameraError, setCameraError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errorCount, setErrorCount] = useState(0);
	const [isQrReaderActive, setIsQrReaderActive] = useState(false);
	const [adminAuthModal, setAdminAuthModal] = useState(false);
	const [adminAuthLoading, setAdminAuthLoading] = useState(false);
	const [adminForm] = Form.useForm();
	const pendingCreditMemoAction = useRef(null);
	const [searchParams] = useSearchParams();

	const requiresCajeroAuth = isCajero(country?.code, currentUser?.Admin);

	const runWithCreditMemoAuth = (action) => {
		if (!requiresCajeroAuth) {
			action();
			return;
		}
		pendingCreditMemoAction.current = action;
		setAdminAuthModal(true);
	};

	const validateAdminCredentials = async (username, password) => {
		try {
			const user = await db.users
				.where("No")
				.equals(username)
				.and((u) => !u.deleted)
				.first();

			if (!user || user.password !== password || !user.Admin) {
				return false;
			}

			return true;
		} catch (error) {
			console.error("Error al validar credenciales de administrador:", error);
			return false;
		}
	};

	const handleAdminAuth = async ({ username, password }) => {
		try {
			setAdminAuthLoading(true);

			if (!(await validateAdminCredentials(username, password))) {
				window.messageApi.error("Credenciales de administrador incorrectas");
				return;
			}

			window.messageApi.success("Autorización de administrador concedida");
			setAdminAuthModal(false);
			adminForm.resetFields();
			pendingCreditMemoAction.current?.();
			pendingCreditMemoAction.current = null;
		} catch (error) {
			console.error("Error en autorización de administrador:", error);
			window.messageApi.error("Error durante la autorización");
		} finally {
			setAdminAuthLoading(false);
		}
	};

	const handleCancelAdminAuth = () => {
		setAdminAuthModal(false);
		adminForm.resetFields();
		pendingCreditMemoAction.current = null;
	};

	// Obtener filtro de fecha desde los query params
	const dateFilter = searchParams.get("filter");

	// Determinar si estamos en modo facturas o abonos
	const isInvoiceMode = documentType === "Invoice";
	const isCreditMemoMode = documentType === "Credit Memo";
	const isPendingMode = syncStatus === "pending";

	// Detener el escáner QR cuando showForm cambia
	useEffect(() => {
		if (showForm) {
			setQrModalVisible(false);
			setIsQrReaderActive(false);
		}
	}, [showForm]);

	const handleAddInvoice = () => {
		const action = () => {
			if (isCreditMemoMode) {
				setSelectedInvoice({
					Document_Type: "Credit Memo",
				});
			} else {
				setSelectedInvoice(null);
			}
			setShowForm(true);
		};

		if (isCreditMemoMode) {
			runWithCreditMemoAuth(action);
		} else {
			action();
		}
	};

	const handleEditInvoice = (invoice) => {
		setSelectedInvoice(invoice);
		setShowForm(true);
	};

	const handleInvoiceSaved = () => {
		setShowForm(false);
	};

	const handleCancel = () => {
		setShowForm(false);
	};

	const handleShowCreditMemoModal = () => {
		runWithCreditMemoAuth(() => {
			setQrModalVisible(true);
			setInvoiceNo("");
			setCameraError(false);
			setErrorCount(0);
			setIsQrReaderActive(true);
		});
	};

	const getSourceListForCorrelatives = async () => {
		if (syncStatus === "pending") {
			const pending = await getPendingSyncInvoices({ dateFilter });
			return pending.filter((invoice) => matchesDocumentType(invoice.Document_Type, documentType, country?.code));
		}
		if (dateFilter === "today") {
			const todays = await getTodaysInvoicesByType(documentType);
			// Also fetch todays "Boleta" if country is Peru and documentType is "Invoice"
			if (documentType === "Invoice" && country?.code === COUNTRY_CODES.PERU) {
				const boletas = await getTodaysInvoicesByType("Boleta");
				return [...todays, ...boletas];
			}
			return todays;
		}
		const allInvoices = await getAllInvoices();
		return allInvoices.filter((invoice) => matchesDocumentType(invoice.Document_Type, documentType, country?.code));
	};

	const handleRefreshCorrelatives = async () => {
		if (country?.code !== COUNTRY_CODES.ECUADOR) return;
		const docLabel = isCreditMemoMode ? "abonos" : "facturas";
		try {
			setLoading(true);
			const sourceList = await getSourceListForCorrelatives();
			const refreshResult = await refreshFinalDocumentNumbersForList(
				country,
				sourceList,
				{ documentType }
			);

			if (refreshResult.totalCandidates === 0) {
				window.messageApi.info(
					`No hay ${docLabel} del listado pendientes de actualizar correlativo`
				);
				return;
			}

			const summary = `Correlativos: ${refreshResult.totalCandidates} revisadas, ${refreshResult.updated} actualizadas, ${refreshResult.notFound} sin resultado.`;

			if (refreshResult.updated > 0) {
				window.messageApi.success(summary);
			} else {
				window.messageApi.warning(summary);
			}

			if (refreshResult.errors.length > 0) {
				console.warn("Errores al actualizar correlativos:", refreshResult.errors);
			}
		} catch (error) {
			console.error("Error al actualizar correlativos:", error);
			window.messageApi.warning(error?.message || "No se pudieron actualizar los correlativos");
		} finally {
			setLoading(false);
		}
	};

	const handleQrScan = async (result, error) => {
		// Si el escáner no está activo, no procesar nada
		if (!isQrReaderActive) return;

		if (error) {
			setErrorCount((prev) => {
				const newCount = prev + 1;
				// Si hay más de 5 errores en secuencia, considerar que la cámara no funciona
				if (newCount > 5 && !cameraError) {
					console.error(
						"Demasiados errores de cámara, deshabilitando escáner QR"
					);
					setCameraError(true);
					window.messageApi.error(
						"Cámara no disponible o con problemas. Por favor, use la entrada manual."
					);
				}
				return newCount;
			});
			return;
		}

		// Resetear contador de errores si se recibe un resultado exitoso
		if (errorCount > 0) {
			setErrorCount(0);
		}

		if (result && !loading) {
			const data = result?.text;
			setLoading(true);
			try {
				await processInvoiceNo(data);
			} finally {
				setLoading(false);
			}
		}
	};

	const handleManualInput = async () => {
		if (!invoiceNo.trim()) {
			window.messageApi.error("Por favor, introduzca un número de factura");
			return;
		}

		setLoading(true);
		try {
			await processInvoiceNo(invoiceNo);
		} finally {
			setLoading(false);
		}
	};

	const processInvoiceNo = async (no) => {
		try {
			let invoiceInput = no.trim();
			let invoice = null;

			// Eliminar ceros a la izquierda si es un número
			const cleanedInput = invoiceInput.replace(/^0+/, "");

			// Intentar buscar primero por ID (asumiendo que es un número)
			if (!isNaN(cleanedInput) && cleanedInput !== "") {
				const invoiceId = parseInt(cleanedInput, 10);

				// Intentar encontrar la factura por ID
				try {
					invoice = await getCompleteInvoice(invoiceId);
				} catch (error) {
					console.log(
						"No se encontró la factura por ID, intentando por número de factura:",
						error
					);
				}
			}

			// Si no se encontró por ID, buscar por número de factura
			if (!invoice) {
				invoice = await getInvoiceByNo(invoiceInput);
			}

			if (!invoice) {
				window.messageApi.error(
					`No se encontró ninguna factura con el número o ID ${invoiceInput}`
				);
				return;
			}

			// Verificar que la factura sea de tipo Invoice o Boleta, no Credit Memo
			if (invoice.Document_Type !== "Invoice" && invoice.Document_Type !== "Boleta") {
				window.messageApi.error(
					"Solo se pueden crear notas de crédito a partir de facturas, no de otras notas de crédito"
				);
				return;
			}

			//Validar que la factura este contabilizada (posted)
			await getFinalDocumentNo(invoice, country);

			invoice = await getCompleteInvoice(invoice.id); //recupero la información actualizada

			if (isEmpty(invoice.Pre_Assigned_No)) {
				window.messageApi.error(
					"Para realizar la nota de crédito la factura debe estar contabilizada."
				);
				return;
			}

			// Si la factura se encontró por No, obtener factura completa con líneas
			let completeInvoice = invoice;
			if (!invoice.lines) {
				completeInvoice = await getCompleteInvoice(invoice.id);
			}

			if (!completeInvoice) {
				window.messageApi.error(
					"No se pudo obtener la información completa de la factura"
				);
				return;
			}

			if (!completeInvoice.lines || completeInvoice.lines.length === 0) {
				window.messageApi.error("La factura no tiene líneas para abonar");
				return;
			}

			// Obtener las líneas de pago de la factura original
			let paymentLines = [];
			let hadMultiplePaymentMethods = false;
			try {
				paymentLines = await getInvoicePaymentLines(completeInvoice);
				// Añadir las líneas de pago a la factura completa para tenerlas disponibles
				completeInvoice.paymentLines = paymentLines;
				// Verificar si la factura original tenía múltiples formas de pago
				hadMultiplePaymentMethods = paymentLines.length > 1;
			} catch (error) {
				console.error("Error al obtener líneas de pago:", error);
				window.messageApi.warning(
					"No se pudieron obtener las formas de pago originales"
				);
			}

			// Buscar todos los abonos existentes para esta factura
			let existingCreditMemos = [];
			if (completeInvoice.No) {
				existingCreditMemos = await getCreditMemosForInvoice(
					completeInvoice.No
				);
			}

			// Crear un mapa de cantidades ya abonadas por producto
			const abonedQuantities = {};

			// Procesar todos los abonos existentes para esta factura
			for (const creditMemo of existingCreditMemos) {
				// Obtener las líneas completas de este abono si no las tiene
				let creditMemoWithLines = creditMemo;
				if (!creditMemo.lines) {
					creditMemoWithLines = await getCompleteInvoice(creditMemo.id);
				}

				// Acumular las cantidades abonadas por número de línea y producto
				if (creditMemoWithLines.lines) {
					for (const line of creditMemoWithLines.lines) {
						const key = `${line.No}-${line.Description}`;
						// Las cantidades en abonos son negativas, las convertimos a positivas para acumularlas
						const abonedQuantity = Math.abs(line.Quantity || 0);

						if (!abonedQuantities[key]) {
							abonedQuantities[key] = 0;
						}
						abonedQuantities[key] += abonedQuantity;
					}
				}
			}

			// Crear líneas para el nuevo abono, teniendo en cuenta las cantidades ya abonadas
			const creditMemoLines = [];
			let hasProductsToReturn = false;

			for (const line of completeInvoice.lines) {
				const key = `${line.No}-${line.Description}`;
				const originalQuantity = Math.abs(line.Quantity || 0);
				const alreadyAbonedQuantity = abonedQuantities[key] || 0;
				const remainingQuantity = originalQuantity - alreadyAbonedQuantity;

				// Solo incluir líneas con cantidades disponibles para abonar
				if (remainingQuantity > 0) {
					hasProductsToReturn = true;
					creditMemoLines.push({
						...line,
						id: undefined, // Nueva línea
						originalQuantity: originalQuantity, // Cantidad original para referencia
						remainingQuantity: remainingQuantity, // Cantidad disponible para abonar
						alreadyAbonedQuantity: alreadyAbonedQuantity, // Cantidad ya abonada
						Quantity: -remainingQuantity, // Por defecto abonar todo lo que queda
						invoiceId: undefined, // Se asignará al guardar
					});
				}
			}

			// Verificar si queda algo por abonar
			if (!hasProductsToReturn) {
				window.messageApi.error(
					"Todos los productos de esta factura ya han sido abonados completamente"
				);
				return;
			}

			// Calcular el total exacto de productos que se van a devolver (incluyendo IVA)
			// Calcular el total exacto de productos que se van a devolver ahora (incluyendo IVA)
			let returnedSubtotal = 0;
			let returnedVat = 0;

			creditMemoLines.forEach((line) => {
				const quantity = Math.abs(line.Quantity || 0);
				const unitPrice = line.Unit_Price || 0;
				const discount = line.Line_Discount_ || 0;
				const discountAmount = unitPrice * (discount / 100);
				const finalPrice = unitPrice - discountAmount;
				const lineSubtotal = quantity * finalPrice;

				// Aplicar IVA específico del producto
				const lineIva = line.PorIva || 0;
				const lineVat = lineSubtotal * (lineIva / 100);

				returnedSubtotal += lineSubtotal;
				returnedVat += lineVat;
			});

			const returnedTotalWithVat = returnedSubtotal + returnedVat;

			// Crear líneas de pago automáticamente basadas en la factura original
			// pero con importes que sumen exactamente el total de productos devueltos (incluyendo IVA)
			const creditMemoPaymentLines = [];
			if (paymentLines && paymentLines.length > 0 && returnedTotalWithVat > 0) {
				// Calcular la suma total de las líneas de pago originales para mantener proporciones
				const originalPaymentTotal = paymentLines.reduce(
					(sum, line) => sum + Math.abs(line.Amount || 0),
					0
				);

				for (let i = 0; i < paymentLines.length; i++) {
					const originalPaymentLine = paymentLines[i];

					// Calcular la proporción que representa esta forma de pago del total original
					const originalAmount = Math.abs(originalPaymentLine.Amount || 0);
					const paymentProportion =
						originalPaymentTotal > 0
							? originalAmount / originalPaymentTotal
							: 0;

					// Aplicar esa misma proporción al total de productos devueltos (con IVA)
					let proportionalAmount = returnedTotalWithVat * paymentProportion;

					// Solo aplicar descuento si la factura original tenía UNA SOLA forma de pago
					// Si había múltiples formas de pago, no se aplicó descuento en la factura original
					const paymentDiscount = originalPaymentLine.Dto || 0;
					if (paymentDiscount > 0 && !hadMultiplePaymentMethods) {
						// Aplicar el descuento solo si era una forma de pago única en la factura original
						proportionalAmount =
							proportionalAmount * (1 - paymentDiscount / 100);
					}

					creditMemoPaymentLines.push({
						id: Date.now() + i, // ID temporal único
						Payment_Method: originalPaymentLine.Payment_Method,
						Amount: proportionalAmount, // Importe exacto proporcional (con IVA y descuento aplicado solo si corresponde)
						Dto: hadMultiplePaymentMethods ? 0 : originalPaymentLine.Dto || 0, // Guardar descuento solo si había una sola forma de pago
						Line_No: i + 1,
						// Marcar como línea automática para evitar edición manual
						isAutoGenerated: true,
						originalPaymentLineId: originalPaymentLine.id,
						// Guardar información original para recálculos posteriores
						originalAmount: originalAmount,
						paymentProportion: paymentProportion,
					});
				}
			}

			// Crear una nueva factura basada en la original pero con tipo "Credit Memo"
			const clientFromInvoice = await getClientByNoOrId(
				completeInvoice.Sell_to_Customer_No_ || completeInvoice.clientLocalId
			);

			const creditMemo = {
				...completeInvoice,
				id: undefined, // Crear nueva factura
				No: undefined, // No sincronizada aún
				Document_Type: "Credit Memo",
				lastSynced: null,
				pendingSync: 1,
				// Usar fechas actuales para la nota de crédito, no las de la factura original
				Posting_Date: new Date().toISOString().split('T')[0], // Fecha actual
				Document_Date: new Date().toISOString().split('T')[0], // Fecha actual
				Applies_to_Doc_Type: completeInvoice.Document_Type,
				Applies_to_Doc_No: invoice.No,
				E_Mail: completeInvoice.E_Mail || clientFromInvoice?.E_Mail || "",
				Telefono: completeInvoice.Telefono || clientFromInvoice?.Telefono || "",
				Mobil: completeInvoice.Mobil || clientFromInvoice?.Mobil || "",
				lines: creditMemoLines,
				// Incluir líneas de pago automáticamente calculadas
				paymentLines: creditMemoPaymentLines,
				// Guardar las formas de pago originales de la factura para referencia
				originalPaymentMethods:
					completeInvoice.paymentLines?.map((line) => line.Payment_Method) ||
					[],
				// Indicar si la factura original tenía múltiples formas de pago (para no aplicar descuentos)
				hadMultiplePaymentMethods: hadMultiplePaymentMethods,
			};

			// Detener el escáner QR completamente antes de mostrar el formulario
			setQrModalVisible(false);
			setIsQrReaderActive(false);

			// Esperar un poco para asegurar que el escáner se ha detenido
			setTimeout(() => {
				setSelectedInvoice(creditMemo);
				setShowForm(true);
				window.messageApi.success(
					`${isCreditMemoMode ? "Abono" : "Nota de crédito"} ${isCreditMemoMode ? "creado" : "creada"
					} a partir de la factura ${no}`
				);
			}, 100);
		} catch (error) {
			console.error("Error al procesar la factura:", error);
			let mensaje = formatAxiosError(error, "No se pudo procesar la factura");
			window.messageApi.error(
				mensaje,
				{ modal: true }
			);
		}
	};

	const handleCloseQrModal = () => {
		setQrModalVisible(false);
		setIsQrReaderActive(false);
		setErrorCount(0);
		setCameraError(false);
	};

	return (
		<div className="invoice-management-container">
			{showForm ? (
				<InvoiceForm
					invoice={selectedInvoice}
					onSave={handleInvoiceSaved}
					onCancel={handleCancel}
				/>
			) : (
				<React.Fragment>
					<Row
						justify="space-between"
						align="middle"
						style={{ marginBottom: 16 }}
					>
						<Col>
							<h1>
								{isPendingMode
									? dateFilter === "today"
										? "Documentos pendientes de hoy"
										: "Documentos pendientes de sincronizar"
									: isInvoiceMode
										? dateFilter === "today"
											? "Facturas de hoy"
											: "Gestión de facturas"
										: dateFilter === "today"
											? "Abonos de hoy"
											: "Gestión de abonos"}
							</h1>
						</Col>
						{(!isPendingMode ||
							((isInvoiceMode || isCreditMemoMode) &&
								country?.code === COUNTRY_CODES.ECUADOR)) && (
								<Col>
									{!isPendingMode && isInvoiceMode && (
										<Button
											type="primary"
											icon={<PlusOutlined />}
											onClick={handleAddInvoice}
											style={{ marginRight: 8 }}
										>
											Nueva Factura
										</Button>
									)}
									{!isPendingMode && isInvoiceMode && showNotasCredito && (
										<Button
											type="primary"
											icon={<ScanOutlined />}
											onClick={handleShowCreditMemoModal}
											style={{
												marginRight:
													country?.code === COUNTRY_CODES.ECUADOR ? 8 : 0,
											}}
										>
											Crear nota de crédito
										</Button>
									)}
									{!isPendingMode && isCreditMemoMode && showNotasCredito && (
										<Button
											type="primary"
											icon={<ScanOutlined />}
											onClick={handleShowCreditMemoModal}
											style={{
												marginRight:
													country?.code === COUNTRY_CODES.ECUADOR ? 8 : 0,
											}}
										>
											Crear desde factura
										</Button>
									)}
									{(isInvoiceMode || isCreditMemoMode) &&
										country?.code === COUNTRY_CODES.ECUADOR && (
											<Button
												type="primary"
												icon={<SyncOutlined />}
												onClick={handleRefreshCorrelatives}
												loading={loading}
												style={{ marginLeft: isPendingMode ? 0 : 8 }}
											>
												Actualizar correlativos
											</Button>
										)}
								</Col>
							)}
					</Row>

					<InvoiceList
						onEdit={handleEditInvoice}
						processInvoiceForCreditMemo={(no) =>
							runWithCreditMemoAuth(() => processInvoiceNo(no))
						}
						documentType={documentType}
						syncStatus={syncStatus}
						dateFilter={dateFilter}
					/>
				</React.Fragment>
			)}

			<Modal
				title={
					isCreditMemoMode
						? "Crear abono desde factura"
						: "Crear nota de crédito desde factura"
				}
				open={qrModalVisible && !isPendingMode}
				onCancel={handleCloseQrModal}
				footer={null}
				width={500}
			>
				<div className="credit-memo-modal">
					<div className="qr-reader-container">
						{!cameraError && isQrReaderActive && qrModalVisible ? (
							<>
								<QrReader
									constraints={{ facingMode: "environment" }}
									onResult={handleQrScan}
									style={{ width: "100%" }}
									scanDelay={1000}
								/>
								<div className="scanner-overlay-text">
									<p>Escanee el código de barras o QR de la factura</p>
								</div>
							</>
						) : (
							<div className="camera-error">
								<p>
									No se pudo acceder a la cámara o ha habido demasiados errores.
									Por favor, introduzca el número manualmente.
								</p>
							</div>
						)}
					</div>

					<div className="manual-input-container">
						<p>O introduzca el número o ID de factura manualmente:</p>
						<p className="input-help-text">
							Puede introducir el número de factura o el ID con o sin ceros a la
							izquierda
						</p>
						<Input
							value={invoiceNo}
							onChange={(e) => setInvoiceNo(e.target.value)}
							placeholder="Número o ID de factura"
							style={{ marginBottom: 16 }}
						/>
						<Button
							type="primary"
							onClick={handleManualInput}
							loading={loading}
						>
							Aceptar
						</Button>
					</div>
				</div>
			</Modal>

			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
						<LockOutlined />
						<span>Autorización de Administrador</span>
					</div>
				}
				open={adminAuthModal}
				onCancel={handleCancelAdminAuth}
				footer={null}
				centered
				maskClosable={false}
			>
				<div style={{ marginBottom: "16px" }}>
					<Alert
						message="Autorización requerida"
						description="Se requiere autorización de supervisor para crear notas de crédito."
						type="warning"
						showIcon
					/>
				</div>

				<Form form={adminForm} layout="vertical" onFinish={handleAdminAuth}>
					<Form.Item
						name="username"
						label="Usuario"
						rules={[{ required: true, message: "Ingrese el usuario de administrador" }]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Usuario de administrador"
							autoComplete="username"
						/>
					</Form.Item>

					<Form.Item
						name="password"
						label="Contraseña"
						rules={[{ required: true, message: "Ingrese la contraseña de administrador" }]}
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Contraseña de administrador"
							autoComplete="current-password"
						/>
					</Form.Item>

					<div style={{ textAlign: "right", marginTop: "24px" }}>
						<Button onClick={handleCancelAdminAuth} style={{ marginRight: "8px" }}>
							Cancelar
						</Button>
						<Button type="primary" htmlType="submit" loading={adminAuthLoading}>
							Autorizar
						</Button>
					</div>
				</Form>
			</Modal>
		</div>
	);
};

export default InvoiceManagement;
